import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { FaTimes, FaExchangeAlt, FaUser, FaUsers, FaCheck } from 'react-icons/fa';
import { IP } from "../configuracion.js";
import "../styles/TransferPedidoSelector.css";
import { useProperty } from "../context/PropertyContext";

const TransferPedidoSelector = ({
  revenues,
  onTransferComplete,
  onClose,
  currentCheque,
  propertyConfig,
  // currentOrder is accepted but not needed here
}) => {
  const { property } = useProperty();

  // ── Productos (origen) ──
  const [pedidos, setPedidos]                           = useState([]);
  const [pedidosSeleccionados, setPedidosSeleccionados] = useState([]);
  const [cargandoPedidos, setCargandoPedidos]           = useState(false);

  // ── Destino ──
  const [selectedRevenue, setSelectedRevenue] = useState(null);
  const [mesas, setMesas]                     = useState([]);
  const [loadingMesas, setLoadingMesas]       = useState(false);
  const [selectedMesa, setSelectedMesa]       = useState(null);
  const [asientoDestino, setAsientoDestino]   = useState(null); // null=sin elegir, 0=nuevo, N=asiento N

  // ── General ──
  const [error, setError]               = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);

  // ✅ Punto 1 — Restringir Transferencia a un mismo Revenue vía config.json.
  // "N" restringe al Revenue de origen del cheque; "Y" (o si la propiedad no
  // tiene esta configuración todavía) conserva el comportamiento actual.
  const permiteOtrosRevenue = propertyConfig?.transfer_other_revenues !== "N";
  const tableRevenues = revenues.filter(r => {
    const esTipoMesa = r.Tipo === "Table" || r.Tipo === "table";
    if (!esTipoMesa) return false;
    return permiteOtrosRevenue ? true : r.id === currentCheque?.revenue_id;
  });

  // ────────────────────────────────────────────────
  // Carga productos de la mesa origen
  // ────────────────────────────────────────────────
  const cargarPedidosMesa = useCallback(async () => {
    if (!currentCheque?.numero_cheque || !currentCheque?.revenue_id) return;
    setCargandoPedidos(true);
    setError(null);
    try {
      const url = `${IP}/tavpos/backend/transferir_pedidos.php?property=${encodeURIComponent(property)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_pedidos_mesa',
          numero_cheque: currentCheque.numero_cheque,
          revenue_id: currentCheque.revenue_id
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        setPedidos(data.pedidos || []);
      } else {
        throw new Error(data.message || "Error al cargar pedidos");
      }
    } catch (err) {
      setError(`Error al cargar productos: ${err.message}`);
    } finally {
      setCargandoPedidos(false);
    }
  }, [currentCheque, property]);

  // ────────────────────────────────────────────────
  // Mesas con cheque abierto para un revenue
  // ────────────────────────────────────────────────
  const fetchMesas = useCallback(async (revenueId) => {
    if (!property || !currentCheque?.numero_cheque) return [];
    try {
      const url = `${IP}/tavpos/backend/transferir_pedidos.php?property=${encodeURIComponent(property)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_mesas_pendientes',
          revenue_id: revenueId,
          excluir_mesa: currentCheque.mesa,
          numero_cheque_origen: currentCheque.numero_cheque
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Error");
      return data.mesas || [];
    } catch (err) {
      setError(`Error al cargar mesas: ${err.message}`);
      return [];
    }
  }, [property, currentCheque]);

  // ────────────────────────────────────────────────
  // Selección de revenue → cargar mesas
  // ────────────────────────────────────────────────
  const handleSelectRevenue = useCallback(async (revenue) => {
    if (!revenue?.id) return;
    setSelectedRevenue(revenue);
    setSelectedMesa(null);
    setAsientoDestino(null);
    setMesas([]);
    setLoadingMesas(true);
    setError(null);
    const result = await fetchMesas(revenue.id);
    setMesas(result);
    setLoadingMesas(false);
  }, [fetchMesas]);

  // ────────────────────────────────────────────────
  // Al montar: cargar productos + auto-seleccionar revenue
  // ────────────────────────────────────────────────
  useEffect(() => {
    cargarPedidosMesa();
    if (tableRevenues.length > 0) {
      const match = tableRevenues.find(
        r => r.name?.toLowerCase() === (currentCheque?.revenue || '').toLowerCase()
      );
      handleSelectRevenue(match || tableRevenues[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ────────────────────────────────────────────────
  // Selección de mesa destino
  // ────────────────────────────────────────────────
  const handleSelectMesa = (mesa) => {
    setSelectedMesa(mesa);
    setAsientoDestino(null);
  };

  // ────────────────────────────────────────────────
  // Ejecutar transferencia
  // ────────────────────────────────────────────────
  const handleTransferir = async () => {
    if (!canTransfer) return;

    const pedidosIds = pedidosSeleccionados.map(id => parseInt(id.split('_')[0]));

    setTransferLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${IP}/tavpos/backend/transferir_pedidos.php?property=${encodeURIComponent(property)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'transfer_pedidos',
            numero_cheque_origen: currentCheque.numero_cheque,
            revenue_id_origen: currentCheque.revenue_id,
            mesa_destino: selectedMesa.mesa,
            revenue_id_destino: selectedRevenue.id,
            pedidos_seleccionados: pedidosIds,
            asiento_destino: asientoDestino,   // 0=nuevo, N=asiento existente
            usuario: JSON.parse(localStorage.getItem('currentUser'))?.nombre || 'Usuario'
          }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Error al transferir");

      const asientoLabel = `Asiento ${asientoDestino}`;

      onTransferComplete?.({
        success: true,
        message: `${pedidosIds.length} producto${pedidosIds.length !== 1 ? 's' : ''} transferido${pedidosIds.length !== 1 ? 's' : ''} a ${selectedMesa.mesa} – ${asientoLabel}`,
        data: data.data
      });
      onClose?.();
    } catch (err) {
      setError(`Error al transferir: ${err.message}`);
    } finally {
      setTransferLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  // Helpers de selección de productos
  // ────────────────────────────────────────────────
  const pedidosPorCliente = pedidos.reduce((acc, p) => {
    const k = p.asiento ?? 0;
    if (!acc[k]) acc[k] = [];
    acc[k].push(p);
    return acc;
  }, {});

  const togglePedido = (pedido, e) => {
    e?.stopPropagation();
    const id = `${pedido.numero_pedido}_${pedido.id_producto}`;
    setPedidosSeleccionados(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleCliente = (cliente) => {
    const ids    = (pedidosPorCliente[cliente] || []).map(p => `${p.numero_pedido}_${p.id_producto}`);
    const allSel = ids.every(id => pedidosSeleccionados.includes(id));
    if (allSel) {
      setPedidosSeleccionados(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setPedidosSeleccionados(prev => {
        const next = [...prev];
        ids.forEach(id => { if (!next.includes(id)) next.push(id); });
        return next;
      });
    }
  };

  const toggleTodos = () => {
    const all = pedidos.map(p => `${p.numero_pedido}_${p.id_producto}`);
    setPedidosSeleccionados(pedidosSeleccionados.length === all.length ? [] : all);
  };

  const fmt = (v) => parseFloat(v || 0).toFixed(2);

  const totalSeleccionado = pedidos
    .filter(p => pedidosSeleccionados.includes(`${p.numero_pedido}_${p.id_producto}`))
    .reduce((s, p) => s + parseFloat(p.precio) * parseInt(p.cantidad), 0);

  const numAsientosDestino = parseInt(selectedMesa?.cliente || 1);

  const canTransfer =
    pedidosSeleccionados.length > 0 &&
    selectedMesa !== null &&
    asientoDestino !== null &&
    !transferLoading;

  const btnLabel = (() => {
    if (transferLoading) return 'Transfiriendo…';
    if (pedidosSeleccionados.length === 0) return 'Selecciona productos';
    if (!selectedMesa) return 'Selecciona una mesa';
    if (asientoDestino === null) return 'Selecciona un asiento';
    const asientoLabel = `Asiento ${asientoDestino}`;
    return `Transferir ${pedidosSeleccionados.length} ítem${pedidosSeleccionados.length !== 1 ? 's' : ''} → ${selectedMesa.mesa} · ${asientoLabel}`;
  })();

  // ────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────
  return (
    <div className="transfer-pedido-selector">

      {/* ── HEADER ── */}
      <div className="transfer-header">
        <div className="transfer-header-info">
          <h2><FaExchangeAlt size={15} style={{ marginRight: 8 }} />Transferir Pedidos</h2>
          <span className="transfer-desde">
            Desde: <strong>{currentCheque?.mesa}</strong>
            {' · '}Cheque <strong>#{currentCheque?.numero_cheque}</strong>
            {' · '}<strong>{currentCheque?.revenue}</strong>
          </span>
        </div>
        <button className="close-button" onClick={onClose}><FaTimes /></button>
      </div>

      {/* ── ERROR BANNER ── */}
      {error && <div className="transfer-error-banner">{error}</div>}

      {/* ── SPLIT BODY ── */}
      <div className="transfer-split">

        {/* ════ PANEL IZQUIERDO: Productos ════ */}
        <div className="transfer-panel transfer-panel--left">
          <div className="transfer-panel-title">
            Productos a transferir
            {pedidosSeleccionados.length > 0 && (
              <span className="panel-badge">{pedidosSeleccionados.length} sel.</span>
            )}
          </div>

          {cargandoPedidos ? (
            <div className="loading-message">Cargando productos…</div>
          ) : pedidos.length === 0 ? (
            <div className="empty-message">No hay productos para transferir</div>
          ) : (
            <>
              <div className="pedidos-header">
                <label className="checkbox-label">
                  <input type="checkbox"
                    checked={pedidosSeleccionados.length === pedidos.length && pedidos.length > 0}
                    ref={el => {
                      if (el) el.indeterminate =
                        pedidosSeleccionados.length > 0 && pedidosSeleccionados.length < pedidos.length;
                    }}
                    onChange={toggleTodos}
                    className="checkbox" />
                  Todos
                </label>
                <span className="count">{pedidosSeleccionados.length}/{pedidos.length}</span>
              </div>

              <div className="clientes-container">
                {Object.keys(pedidosPorCliente).sort((a, b) => a - b).map(cliente => {
                  const ps       = pedidosPorCliente[cliente];
                  const ids      = ps.map(p => `${p.numero_pedido}_${p.id_producto}`);
                  const allSel   = ids.every(id => pedidosSeleccionados.includes(id));
                  const someSel  = ids.some(id => pedidosSeleccionados.includes(id)) && !allSel;
                  const totalCli = ps.reduce((s, p) => s + parseFloat(p.precio) * parseInt(p.cantidad), 0);

                  return (
                    <div key={cliente} className="cliente-section">
                      <div className="cliente-header">
                        <label className="checkbox-label">
                          <input type="checkbox" checked={allSel}
                            ref={el => { if (el) el.indeterminate = someSel; }}
                            onChange={() => toggleCliente(cliente)}
                            className="checkbox" />
                          <FaUser className="client-icon" size={11} />
                          Asiento {parseInt(cliente) + 1}
                        </label>
                        <span className="cliente-total">${fmt(totalCli)}</span>
                      </div>

                      <div className="pedidos-cliente-list">
                        {ps.map(pedido => {
                          const pid = `${pedido.numero_pedido}_${pedido.id_producto}`;
                          const sel = pedidosSeleccionados.includes(pid);
                          return (
                            <div key={pid}
                              className={`pedido-item${sel ? ' selected' : ''}`}
                              onClick={e => togglePedido(pedido, e)}>
                              <input type="checkbox" checked={sel}
                                onChange={e => { e.stopPropagation(); togglePedido(pedido, e); }}
                                className="checkbox"
                                onClick={e => e.stopPropagation()} />
                              <div className="pedido-info">
                                <div className="pedido-name">{pedido.nombre}</div>
                                <div className="pedido-details">
                                  <span>×{pedido.cantidad}</span>
                                  <span>${fmt(pedido.precio)}</span>
                                  <span className={`estado-badge ${(pedido.estado || '').toLowerCase()}`}>
                                    {pedido.estado || 'PENDIENTE'}
                                  </span>
                                </div>
                                {pedido.modificadores?.length > 0 && (
                                  <div className="modificadores">
                                    {pedido.modificadores.length} modificador{pedido.modificadores.length !== 1 ? 'es' : ''}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalSeleccionado > 0 && (
                <div className="total-section">
                  Total seleccionado: <strong>${fmt(totalSeleccionado)}</strong>
                </div>
              )}
            </>
          )}
        </div>

        {/* ════ PANEL DERECHO: Destino ════ */}
        <div className="transfer-panel transfer-panel--right">
          <div className="transfer-panel-title">Destino</div>

          {/* Revenue chips */}
          <div className="revenue-chips">
            {tableRevenues.length === 0 ? (
              <p className="no-results">No hay áreas de tipo mesa disponibles</p>
            ) : (
              tableRevenues.map(r => (
                <button key={r.id}
                  className={`revenue-chip${selectedRevenue?.id === r.id ? ' active' : ''}`}
                  onClick={() => handleSelectRevenue(r)}>
                  {r.name}
                </button>
              ))
            )}
          </div>

          {/* Mesas grid */}
          {selectedRevenue && (
            <>
              <div className="mesas-section-title">
                Mesas con cheque abierto — <strong>{selectedRevenue.name}</strong>
              </div>

              {loadingMesas ? (
                <div className="loading-message">Cargando mesas…</div>
              ) : mesas.length === 0 ? (
                <div className="no-available-spaces">
                  <p>Sin mesas disponibles en {selectedRevenue.name}</p>
                  <small>Las mesas deben tener un cheque con pago pendiente</small>
                </div>
              ) : (
                <div className="mesas-grid">
                  {mesas.map(mesa => {
                    const isSel     = selectedMesa?.numero_cheque === mesa.numero_cheque;
                    const nAsientos = parseInt(mesa.cliente || 1);
                    return (
                      <div key={mesa.numero_cheque}
                        className={`mesa-card${isSel ? ' mesa-card--selected' : ''}`}
                        onClick={() => handleSelectMesa(mesa)}>
                        {isSel && <FaCheck className="mesa-card-check" size={9} />}
                        <div className="mesa-card-name">{mesa.mesa}</div>
                        <div className="mesa-card-seats">
                          <FaUsers size={9} />
                          {' '}{nAsientos} asiento{nAsientos !== 1 ? 's' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Selector de asiento destino */}
              {selectedMesa && (
                <div className="asiento-selector">
                  <div className="asiento-selector-title">
                    ¿A qué asiento de <strong>{selectedMesa.mesa}</strong>?
                  </div>
                  <div className="asiento-buttons">
                    {Array.from({ length: numAsientosDestino }, (_, i) => (
                      <button key={i + 1}
                        className={`asiento-btn${asientoDestino === i + 1 ? ' asiento-btn--active' : ''}`}
                        onClick={() => setAsientoDestino(i + 1)}>
                        <FaUser size={9} />
                        {' '}Asiento {i + 1}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div className="transfer-footer">
        <button className="cancel-button" onClick={onClose} disabled={transferLoading}>
          Cancelar
        </button>
        <button
          className={`transfer-action-btn${canTransfer ? '' : ' disabled'}`}
          disabled={!canTransfer}
          onClick={handleTransferir}>
          {btnLabel}
        </button>
      </div>
    </div>
  );
};

TransferPedidoSelector.propTypes = {
  revenues:           PropTypes.array.isRequired,
  onTransferComplete: PropTypes.func,
  onClose:            PropTypes.func.isRequired,
  currentCheque:      PropTypes.object.isRequired,
  currentOrder:       PropTypes.array,
  propertyConfig:     PropTypes.object,
};

export default TransferPedidoSelector;
